import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import { FreightBrowser } from '@shipstation/client-common/components/freight-browser';
import { getQuotes, saveQuoteToFulfillmentPlan } from '@shipstation/client-common/api/ltl.api';
import {
  mapBrowserServicesToAccessorials,
  mapDimensionUnit,
  mapHandlingUnitsToPayload,
  mapWeightUnit,
  mapWizardInsurance,
} from '@shipstation/client-common/ltl/freight-payload-mappers';
import { useFreightQuoteProgress } from '@shipstation/client-common/pusher/hooks/use-freight-quote-progress';
import { LTL_PUSHER_TIMEOUT_MS, makeLtlPusherService } from '@shipstation/client-common/pusher/ltl-fallback-helpers';
import { WIZARD_MODE } from '@shipstation/client-common/components/freight-booking-wizard';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService } from 'ui-components/services/notification-service';
import { mapFreightFormToRateBrowserDefaults } from '../../../../components/modals/rate-browser-modal/freight-to-rate-browser-defaults';
import { PusherService } from '../../../../services/pusher-service';
import Global from '../../../../store/global-selectors';
import { getFreightGridRequest } from '../../../../store/ltl/ltl.actions';
import { openRateBrowserModal } from '../../../../store/rate-browser/rate-browser.actions';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import { useModal } from '../../../../hooks/use-modal';
import { useEnableLtlFreight } from '../../../../feature-flags/hooks/use-enable-ltl-freight';
import ShipFromModal from '../../../../ship-from/modals';
import { FreightBookingWizardContainer } from '../freight-booking-wizard/freight-booking-wizard-container';
import { useTrackBrowseFreightRatesClick } from '../../../../tracking/use-track-browse-freight-rates-click';
import { Segment } from '../../../../services/analytics-services/segment';
import { LtlFreightTrackingEvents } from '../../tracking-events';
import { freightBrowserContainerMessages } from './messages';

import type { ReactNode } from 'react';

import type { ShipFrom } from '@shipstation/client-common/types/ship-from';
import type { BaseWeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';

import type { FreightBookingFormState } from '@shipstation/client-common/components/freight-booking-wizard';
import type { FreightBrowserFormState } from '@shipstation/client-common/components/freight-browser';
import type { AsyncTaskErrorEnvelope } from '@shipstation/client-common/types/common';
import type { FreightQuoteRequest, FreightFulfillmentPlanSaveQuoteRequest } from '@shipstation/client-common/types/freight-requests';
import type { LtlAddress, LtlAdditionalServices, LtlHandlingUnit, LtlQuote } from '@shipstation/client-common/types/ltl';

export const DEFAULT_ADDITIONAL_SERVICES: LtlAdditionalServices = {
  isLiftgatePickup: false,
  isInsidePickup: false,
  isResidentialPickup: false,
  isLimitedAccessPickup: false,
  isFlatbedPickup: false,
  isDockLevelPickup: false,
  isTopLoadPickup: false,
  isConstructionSitePickup: false,
  isTradeShowPickup: false,
  tradeshowPickupName: null,
  tradeshowPickupBoothNumber: null,
  isGroceryConsolidationPickup: false,
  isNotifyBeforePickup: false,
  isLiftgateDelivery: false,
  isInsideDelivery: false,
  isResidentialDelivery: false,
  isLimitedAccessDelivery: false,
  isFlatbedDelivery: false,
  isDockLevelDelivery: false,
  isConstructionSiteDelivery: false,
  isTradeShowDelivery: false,
  tradeshowDeliveryName: null,
  tradeshowDeliveryBoothNumber: null,
  isGroceryConsolidationDelivery: false,
  isAppointmentDelivery: false,
  isCallBeforeDelivery: false,
  isSortAndSegregate: false,
  isNotifyBeforeDelivery: false,
  isHazmat: false,
  isTemperatureControl: false,
  isProtectFromFreezing: false,
  isOverlength8to12ft: false,
  isOverlength12to20ft: false,
  isOverlength20ftPlus: false,
  isGuaranteedDelivery: false,
  isBlindShipment: false,
  isMarkAndTag: false,
  isCOD: false,
};

const EMPTY_ADDRESS: LtlAddress = {
  name: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: 'US',
  phone: '',
  email: '',
  locationType: '',
};

// Builds the booking-wizard seed form-state from the browser's in-memory form. The
// browser only collects a subset of the wizard's fields, so unmapped wizard fields
// fall back to empty defaults. The wizard resolves the origin address itself from
// selectedShipFromId, so originAddress is left empty here.
const buildBookingFormState = (
  browserForm: FreightBrowserFormState,
  sellerFreightProviderId: number | undefined,
): FreightBookingFormState => {
  const services = browserForm.additionalServices;
  return {
    selectedShipFromId: browserForm.shipFromId,
    originAddress: EMPTY_ADDRESS,
    shipmentDate: '',
    pickupServices: {
      isInsidePickup: services.isInsidePickup,
      isConstructionSitePickup: services.isConstructionSitePickup,
      isLiftgatePickup: services.isLiftgatePickup,
      isNotifyBeforePickup: services.isNotifyBeforePickup,
      isResidentialPickup: services.isResidentialPickup,
      isDropAtTerminal: false,
      isGroceryConsolidationPickup: services.isGroceryConsolidationPickup,
      isLimitedAccess: services.isLimitedAccessPickup,
      isTradeshowPickup: services.isTradeShowPickup,
    },
    destinationAddress: {
      ...EMPTY_ADDRESS,
      city: browserForm.destination.city,
      state: browserForm.destination.state,
      postalCode: browserForm.destination.postalCode,
      countryCode: browserForm.destination.countryCode || 'US',
    },
    shouldSaveAsNewCustomer: false,
    deliveryServices: {
      isInsideDelivery: services.isInsideDelivery,
      isConstructionSiteDelivery: services.isConstructionSiteDelivery,
      isLiftgateDelivery: services.isLiftgateDelivery,
      isNotifyBeforeDelivery: services.isNotifyBeforeDelivery,
      isResidentialDelivery: services.isResidentialDelivery,
      isHoldAtTerminal: false,
      isGroceryConsolidationDelivery: services.isGroceryConsolidationDelivery,
      isAppointmentDelivery: services.isAppointmentDelivery,
      isLimitedAccess: services.isLimitedAccessDelivery,
      isTradeshowDelivery: services.isTradeShowDelivery,
    },
    tradeshow: { name: '', boothNumber: '' },
    tradeshowPickup: { name: '', boothNumber: '' },
    handlingUnits: browserForm.handlingUnits,
    shipmentServices: {
      isSortAndSegregate: services.isSortAndSegregate,
      isProtectFromFreeze: services.isProtectFromFreezing,
    },
    pickupReadyTime: '09:00',
    pickupCloseTime: '17:00',
    willArrangePickupSeparately: false,
    insurance: {
      isInsured: browserForm.insurance.isInsured,
      conditionOfGoods: browserForm.insurance.conditionOfGoods,
      category: browserForm.insurance.category,
      insuredValue: browserForm.insurance.insuredValue,
      marksAndNumbers: browserForm.insurance.marksAndNumbers,
    },
    handlingCharge: { isEnabled: false, value: 0 },
    references: { isEnabled: false, items: [] },
    notifications: { isEnabled: false, recipientConfigs: [] },
    pickupInstructions: '',
    deliveryInstructions: '',
    handlingInstructions: '',
    shouldSaveLtlDefaults: false,
    sellerFreightProviderID: sellerFreightProviderId,
  };
};

const INITIAL_FORM_STATE: Omit<FreightBrowserFormState, 'handlingUnits'> = {
  shipFromId: '',
  destination: { city: '', state: '', postalCode: '', countryCode: 'US' },
  additionalServices: DEFAULT_ADDITIONAL_SERVICES,
  insurance: { isInsured: false, conditionOfGoods: '', category: '', insuredValue: 0, marksAndNumbers: '' },
};

const AUTO_BROWSE_RATES_DEBOUNCE_MS = 600;

type FreightBrowserValidationErrorCode =
  | 'noShipFromSelected'
  | 'commodityWeightRequired'
  | 'commodityFreightClassRequired'
  | 'handlingUnitHeightRequired';

const getFreightBrowserValidationErrorCode = (current: FreightBrowserFormState): FreightBrowserValidationErrorCode | null => {
  if (!current.shipFromId) return 'noShipFromSelected';

  const hasMissingCommodityWeight = current.handlingUnits.some((unit) =>
    unit.commodities.some((commodity) => !commodity.weight || commodity.weight <= 0),
  );
  if (hasMissingCommodityWeight) return 'commodityWeightRequired';

  const hasMissingFreightClass = current.handlingUnits.some((unit) => unit.commodities.some((commodity) => !commodity.freightClass));
  if (hasMissingFreightClass) return 'commodityFreightClassRequired';

  const hasMissingHandlingUnitHeight = current.handlingUnits.some((unit) => !unit.height || unit.height <= 0);
  if (hasMissingHandlingUnitHeight) return 'handlingUnitHeightRequired';

  return null;
};

const mergeWithSeed = (defaults: FreightBrowserFormState, seed: FreightBrowserFormState): FreightBrowserFormState => ({
  ...defaults,
  ...seed,
  destination: { ...defaults.destination, ...seed.destination },
  additionalServices: { ...defaults.additionalServices, ...seed.additionalServices },
  insurance: { ...defaults.insurance, ...seed.insurance },
  handlingUnits: seed.handlingUnits?.length ? seed.handlingUnits : defaults.handlingUnits,
});

type FreightBrowserContainerProps = {
  isOpen: boolean;
  onClose: () => void;
  seedFormState?: FreightBrowserFormState;
};

export const FreightBrowserContainer = ({ isOpen, onClose, seedFormState }: FreightBrowserContainerProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const defaultShipFromId = useSelector(Global.selectSellerDefaultShipFromId);
  const shipFroms: ShipFrom[] = useSelector(Global.selectSellerShipFroms);
  const freightProviders = useSelector(Global.selectSellerFreightProviders);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;
  const trackBrowseFreightRatesClick = useTrackBrowseFreightRatesClick();
  const isHazmatEnabled = useEnableLtlFreight();

  const createDefaultHandlingUnitWithUnits = useCallback(
    (): LtlHandlingUnit => ({
      id: crypto.randomUUID(),
      packagingType: 'Pallet',
      quantity: 1,
      length: 0,
      width: 0,
      height: 0,
      dimensionUnit,
      isStackable: true,
      commodities: [
        {
          id: crypto.randomUUID(),
          description: '',
          freightClass: '',
          nmfcCode: '',
          quantity: 1,
          weight: 0,
          weightUnit,
          isHazmat: false,
          hazmatDetails: null,
          hazmatInfo: null,
          insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
        },
      ],
    }),
    [dimensionUnit, weightUnit],
  );

  const [formState, setFormState] = useState<FreightBrowserFormState>(() => {
    const defaultFormState: FreightBrowserFormState = {
      ...INITIAL_FORM_STATE,
      handlingUnits: [createDefaultHandlingUnitWithUnits()],
      shipFromId: defaultShipFromId != null ? String(defaultShipFromId) : '',
    };
    return seedFormState ? mergeWithSeed(defaultFormState, seedFormState) : defaultFormState;
  });
  const [rates, setRates] = useState<LtlQuote[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [rateError, setRateError] = useState<ReactNode>(undefined);
  const [rateErrorCode, setRateErrorCode] = useState<string | null | undefined>(undefined);
  // When set, the rate browser hands off to the booking wizard seeded with this data.
  const [configureSeed, setConfigureSeed] = useState<{
    formState: FreightBookingFormState;
    rates: LtlQuote[];
    selectedRate: LtlQuote | null;
  } | null>(null);

  const { open: openAddShipFrom, close: closeAddShipFrom, isOpen: isAddShipFromOpen } = useModal();
  const zIndex = useMaxZ();

  const formStateRef = useRef(formState);
  formStateRef.current = formState;

  const workflowGuidRef = useRef<string>('');
  const ratesRef = useRef<LtlQuote[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    workflowGuidRef.current = GuidService.newGuid();
  }, [isOpen]);

  const pendingGuidsRef = useRef<Set<string>>(new Set());

  const pusherService = useMemo(
    () => makeLtlPusherService(pendingGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );

  const { startListening, cancelListening } = useFreightQuoteProgress({
    pusherService,
    pendingGuidsRef,
    onQuotes: (quotes) => {
      // Append to the ref synchronously so onComplete (which can fire in the same
      // tick as the final onQuotes, before a re-render refreshes ratesRef) reconciles
      // against the freshly-arrived rates instead of a stale empty array.
      const next = [...ratesRef.current, ...quotes];
      ratesRef.current = next;
      setRates(next);
    },
    onError: (message, code) => {
      setRateError(message);
      setRateErrorCode(code ?? null);
      setIsLoadingRates(false);
    },
    onComplete: () => {
      setIsLoadingRates(false);
      if (ratesRef.current.length > 0) {
        Segment.trackEvent(LtlFreightTrackingEvents.RATES_RECEIVED, {
          source: 'from_browse_rates' as const,
          workflowGuid: workflowGuidRef.current,
          quote_count: ratesRef.current.length,
        });
      }
    },
    timeoutMs: LTL_PUSHER_TIMEOUT_MS,
  });

  const shipFromOptions = useMemo(() => {
    return shipFroms.map((sf) => ({
      id: String(sf.shipFromId),
      name: sf.name,
      address: sf.originAddress
        ? {
            line1: sf.originAddress.line1,
            line2: sf.originAddress.line2,
            city: sf.originAddress.city,
            state: sf.originAddress.state,
            postalCode: sf.originAddress.postalCode,
            countryCode: sf.originAddress.countryCode,
            ianaTimeZone: sf.originAddress.ianaTimeZone,
          }
        : undefined,
    }));
  }, [shipFroms]);

  const shipFromAddress = useMemo(() => {
    const matched = shipFroms.find((sf) => String(sf.shipFromId) === formState.shipFromId);
    return (
      matched?.originAddress ?? {
        name: '',
        company: null,
        phone: '',
        email: '',
        line1: '',
        line2: '',
        line3: null,
        city: '',
        state: '',
        postalCode: '',
        countryCode: 'US',
        residentialIndicator: 'Unknown' as const,
        ianaTimeZone: null,
      }
    );
  }, [shipFroms, formState.shipFromId]);

  const destinationAddress = useMemo(
    (): LtlAddress => ({
      name: '',
      company: '',
      line1: '',
      line2: '',
      city: formState.destination.city,
      state: formState.destination.state,
      postalCode: formState.destination.postalCode,
      countryCode: formState.destination.countryCode || 'US',
      phone: '',
      email: '',
      locationType: '',
    }),
    [formState.destination],
  );

  const getConnectedProviderId = useCallback((): number | undefined => {
    return freightProviders?.[0]?.sellerFreightProviderId ?? undefined;
  }, [freightProviders]);

  const handleBrowseRates = useCallback(async () => {
    const current = formStateRef.current;
    const sellerFreightProviderId = getConnectedProviderId();
    const validationErrorCode = getFreightBrowserValidationErrorCode(current);
    if (validationErrorCode) {
      setRateErrorCode(undefined);
      setRateError(intl.formatMessage(freightBrowserContainerMessages[validationErrorCode]));
      return;
    }

    trackBrowseFreightRatesClick();

    setIsLoadingRates(true);
    setRateError(undefined);
    setRateErrorCode(undefined);
    setRates([]);
    ratesRef.current = [];
    cancelListening();

    const payload: FreightQuoteRequest = {
      sellerFreightProviderId,
      shipment: {
        shipFromId: Number(current.shipFromId),
        destinationAddress: {
          city: current.destination.city,
          state: current.destination.state,
          postalCode: current.destination.postalCode,
          countryCode: current.destination.countryCode,
        },
        shipmentDate: `${new Date().toISOString().split('T')[0]}T09:00:00`,
        handlingUnits: mapHandlingUnitsToPayload(current.handlingUnits, mapWeightUnit(weightUnit), mapDimensionUnit(dimensionUnit)),
        additionalServices: mapBrowserServicesToAccessorials(current.additionalServices),
        insurance: mapWizardInsurance(current.insurance),
      },
    };

    try {
      const data = (await getQuotes(payload)) as AsyncTaskErrorEnvelope;
      if (data.success === false) {
        setRateError(data.errors?.[0]?.message ?? intl.formatMessage(freightBrowserContainerMessages.failedToRetrieveQuotes));
        setIsLoadingRates(false);
        return;
      }
      const jobGuids: string[] = (data.jobGuids ?? data.JobGuids ?? []) as string[];
      if (jobGuids.length === 0) {
        setIsLoadingRates(false);
        return;
      }
      startListening(jobGuids);
    } catch (err) {
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        (typeof responseData === 'string' ? responseData : null) ??
        (responseData as { Message?: string })?.Message ??
        (err instanceof Error ? err.message : null) ??
        intl.formatMessage(freightBrowserContainerMessages.rateFetchError);
      setRateError(message);
      setIsLoadingRates(false);
    }
  }, [cancelListening, dimensionUnit, getConnectedProviderId, intl, startListening, trackBrowseFreightRatesClick, weightUnit]);

  const isFormReadyForRates = useMemo(() => getFreightBrowserValidationErrorCode(formState) === null, [formState]);

  // A value-keyed snapshot of the fields a quote request is built from. Used as the
  // auto-browse effect's dependency instead of the formState object reference, so a
  // re-render that produces a new formState object with unchanged data (or a
  // handleBrowseRates identity change from an unrelated dependency) doesn't re-fire it.
  const quoteRequestKey = useMemo(() => JSON.stringify(formState), [formState]);

  const handleBrowseRatesRef = useRef(handleBrowseRates);
  handleBrowseRatesRef.current = handleBrowseRates;

  // Auto-browse once every required field is filled, debounced so rapid edits (e.g. typing a
  // weight digit-by-digit) don't fire a request per keystroke. Manual "Browse Rates" clicks
  // still work immediately via handleBrowseRates itself.
  useEffect(() => {
    if (!isFormReadyForRates) return undefined;
    const timeoutId = setTimeout(() => {
      handleBrowseRatesRef.current();
    }, AUTO_BROWSE_RATES_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [quoteRequestKey, isFormReadyForRates]);

  const handleSaveQuote = useCallback(
    async (selectedQuote: LtlQuote | null) => {
      const current = formStateRef.current;
      const sellerFreightProviderId = getConnectedProviderId();

      const payload: FreightFulfillmentPlanSaveQuoteRequest = {
        fulfillmentPlanId: null,
        freightNumber: null,
        sellerFreightProviderId,
        productTransactionId: rates[0]?.productTransactionId,
        offerId: selectedQuote?.offerId ?? null,
        carrierName: selectedQuote?.carrierName ?? null,
        carrierScac: selectedQuote?.carrierScac ?? null,
        serviceName: selectedQuote?.serviceName ?? null,
        totalCharges: selectedQuote?.totalCharges ?? null,
        transitDays: selectedQuote?.transitDays ?? null,
        estimatedDeliveryDate: selectedQuote?.estimatedDeliveryDate ?? null,
        totalWeight: current.handlingUnits.reduce(
          (sum, unit) => sum + unit.commodities.reduce((cSum, c) => cSum + c.weight * c.quantity, 0) * unit.quantity,
          0,
        ),
        shipDate: new Date().toISOString().split('T')[0],
        shipFromWarehouseId: current.shipFromId ? Number(current.shipFromId) : null,
        destinationAddress: {
          city: current.destination.city,
          state: current.destination.state,
          postalCode: current.destination.postalCode,
          countryCode: current.destination.countryCode,
        },
        handlingUnits: mapHandlingUnitsToPayload(current.handlingUnits, mapWeightUnit(weightUnit), mapDimensionUnit(dimensionUnit)),
        additionalServices: mapBrowserServicesToAccessorials(current.additionalServices),
        insurance: mapWizardInsurance(current.insurance),
        allQuotes: rates.length
          ? rates.map((q) => ({
              offerId: q.offerId,
              productTransactionId: q.productTransactionId,
              carrierName: q.carrierName,
              carrierScac: q.carrierScac,
              serviceName: q.serviceName,
              totalCharges: q.totalCharges,
              transitDays: q.transitDays,
              estimatedDeliveryDate: q.estimatedDeliveryDate,
              isGuaranteed: q.isGuaranteed,
              serviceType: q.serviceType ?? null,
              originTerminalCode: q.originTerminalCode ?? null,
              destinationTerminalCode: q.destinationTerminalCode ?? null,
              insuredAmount: q.insuredAmount ?? null,
              insurancePremium: q.insurancePremium ?? null,
              insuranceCertificateNumber: q.insuranceCertificateNumber ?? null,
            }))
          : null,
      };

      try {
        await saveQuoteToFulfillmentPlan(payload);
        NotificationService.showGenericSuccessNotification(
          undefined,
          <FormattedMessage {...freightBrowserContainerMessages.saveQuoteSuccess} />,
        );
        dispatch(getFreightGridRequest());
      } catch (err) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          intl.formatMessage(freightBrowserContainerMessages.saveQuoteFailed);
        NotificationService.showGenericFailureNotification(undefined, errorMessage);
      }
    },
    [dimensionUnit, dispatch, getConnectedProviderId, intl, rates, weightUnit],
  );

  const handleConfigureShipment = useCallback(
    (selectedQuote: LtlQuote) => {
      const current = formStateRef.current;
      // Hand off the in-memory browser form + fetched rates to the booking wizard.
      // The wizard resolves the origin address itself from selectedShipFromId.
      setConfigureSeed({
        formState: buildBookingFormState(current, getConnectedProviderId()),
        rates,
        selectedRate: selectedQuote,
      });
    },
    [getConnectedProviderId, rates],
  );

  const handleCloseWizard = useCallback(() => {
    setConfigureSeed(null);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleBrowseParcelRates = useCallback(() => {
    dispatch(openRateBrowserModal(mapFreightFormToRateBrowserDefaults(formStateRef.current)));
    onClose();
  }, [dispatch, onClose]);

  const handleDensityAction = useCallback(() => {
    Segment.trackEvent(LtlFreightTrackingEvents.CALC_DENSITY_CLICKED);
  }, []);

  const handleAddShipFromSuccess = useCallback(
    (result: object) => {
      const newShipFrom = result as { shipFromId: string };
      closeAddShipFrom();
      setFormState((prev) => ({ ...prev, shipFromId: newShipFrom.shipFromId }));
      NotificationService.showGenericSuccessNotification(undefined, intl.formatMessage(freightBrowserContainerMessages.addShipFromSuccess));
    },
    [closeAddShipFrom, intl],
  );

  if (configureSeed) {
    return (
      <FreightBookingWizardContainer
        isOpen
        mode={WIZARD_MODE.NEW}
        onClose={handleCloseWizard}
        seedFormState={configureSeed.formState}
        seedRates={configureSeed.rates}
        seedSelectedRate={configureSeed.selectedRate}
        source='from_browse_rates'
      />
    );
  }

  return (
    <>
      <FreightBrowser
        destinationAddress={destinationAddress}
        displayDimensionUnit={dimensionUnit}
        displayWeightUnit={weightUnit}
        formState={formState}
        isHazmatEnabled={isHazmatEnabled}
        isLoadingRates={isLoadingRates}
        isOpen={isOpen}
        onAddShipFrom={openAddShipFrom}
        onBrowseParcelRates={handleBrowseParcelRates}
        onBrowseRates={handleBrowseRates}
        onClose={handleClose}
        onConfigureShipment={handleConfigureShipment}
        onDensityAction={handleDensityAction}
        onFormChange={setFormState}
        onSaveQuote={handleSaveQuote}
        rateError={rateError}
        rateErrorCode={rateErrorCode}
        rates={rates}
        sellerFreightProviderId={getConnectedProviderId()}
        shipFromAddress={shipFromAddress}
        shipFromOptions={shipFromOptions}
        zIndex={zIndex}
      />
      {isAddShipFromOpen && (
        <ShipFromModal onClose={closeAddShipFrom} onSuccess={handleAddShipFromSuccess} source={EventSourceStrings.LTL_FREIGHT_BROWSER} />
      )}
    </>
  );
};
